import { Injectable, inject, computed } from '@angular/core';
import type { UserRole } from 'src/app/shared/models/session.model';
import { RoleService } from 'src/app/shared/services/role.service';

export interface MenuItem {
  /** i18n key */
  label: string;
  /** Ionic icon name */
  icon: string;
  /** Router path */
  route: string;
  /** Roles that can see this item */
  roles: UserRole[];
  /** Also highlight on child routes (e.g. /perfect-store/negotiations). Default: exact match only. */
  matchChildren?: boolean;
}

/**
 * Catalog of all menu items annotated with allowed roles.
 *
 * The `MenuComponent` consumes `items` (a computed signal) to render
 * only the items the current user role can see.
 *
 * This service also serves as the single source of truth for the menu
 * structure, usable in tests and for future features (e.g. quick-actions
 * in dashboard consuming the same catalog).
 */
@Injectable({ providedIn: 'root' })
export class MenuConfigService {
  private roleService = inject(RoleService);

  /** Full catalog of all menu items. */
  private readonly catalog: MenuItem[] = [
    { label: 'MENU.ITEMS.DASHBOARD',  icon: 'stats-chart-outline',       route: '/dashboard',          roles: ['admin', 'marketer'] },
    { label: 'MENU.ITEMS.PRODUCTS',   icon: 'cube-outline',              route: '/product',            roles: ['admin'] },
    { label: 'MENU.ITEMS.REPORTS',    icon: 'bar-chart-outline',         route: '/reports',            roles: ['admin'] },
    { label: 'MENU.ITEMS.PERFECT_STORE', icon: 'ribbon-outline',        route: '/perfect-store',      roles: ['admin'], matchChildren: true },
    { label: 'MENU.ITEMS.MARKETERS',  icon: 'people-outline',            route: '/marketer',           roles: ['admin'] },
    { label: 'VISIT.TITLE',          icon: 'clipboard-outline',         route: '/visit',               roles: ['marketer'] },
    { label: 'SALES_POINT.TITLE',   icon: 'storefront-outline',         route: '/sales-point',        roles: ['admin'] },
    { label: 'MENU.ITEMS.CHAINS',   icon: 'link-outline',               route: '/chain',              roles: ['admin'] },
    { label: 'MENU.ITEMS.ROUTES',     icon: 'map-outline',               route: '/route',              roles: ['admin'] },
  ];

  /** Bottom items — always visible to authenticated users. */
  readonly bottomItems: MenuItem[] = [
    { label: 'SETTING.TITLE', icon: 'settings-outline', route: '/setting', roles: ['admin', 'marketer'] },
  ];

  /** Computed menu items filtered by current role. Reactive — no manual refresh. */
  readonly items = computed(() => {
    const role = this.roleService.role();
    if (!role) return [];
    return this.catalog.filter((item) => item.roles.includes(role));
  });
}
